import { IDropdownResponse } from '../models/dynamic.model';
import { LookupDisplaySeparator } from './constants';

export interface AddressLocationFormValue {
  countryCode?: string | null;
  country?: string | null;
  state?: string | null;
  city?: string | null;
}

export type MapLocationLevel = 'world' | 'country' | 'state' | 'city';

export interface AddressMapPreviewContext {
  stateList: IDropdownResponse[];
  stateListCountryCode: string;
  cityValidationList: IDropdownResponse[];
  cityListCountryCode: string;
  stateListRequired: boolean;
}

export interface AddressMapPreviewResolution {
  /** `null` → world map */
  query: string | null;
  needsCityListLoad?: boolean;
  needsStateListLoad?: boolean;
  showCityValidationError?: boolean;
  showStateValidationError?: boolean;
}

export function parseLookupDisplayValue(
  value: string | null | undefined
): string {
  if (!value) {
    return '';
  }
  const trimmed = value.trim();
  if (trimmed.includes(LookupDisplaySeparator)) {
    const parts = trimmed.split(LookupDisplaySeparator);
    return (
      parts.slice(1).join(LookupDisplaySeparator) || parts[0] || ''
    ).trim();
  }
  return trimmed;
}

export function parseLookupCode(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const trimmed = value.trim();
  if (trimmed.includes(LookupDisplaySeparator)) {
    return trimmed.split(LookupDisplaySeparator)[0].trim();
  }
  return trimmed;
}

export function parseCountryNameForMap(
  countryCode?: string | null,
  country?: string | null
): string {
  if (country?.trim()) {
    return parseLookupDisplayValue(country);
  }
  return parseLookupDisplayValue(countryCode);
}

export function parseStateForMap(state?: string | null): string {
  return parseLookupDisplayValue(state);
}

export function parseCityForMap(city?: string | null): string {
  return parseLookupDisplayValue(city);
}

export function hasCountryForMap(value: AddressLocationFormValue): boolean {
  return parseLookupCode(value.countryCode) !== '';
}

export function hasStateForMap(value: AddressLocationFormValue): boolean {
  return (value.state ?? '').trim() !== '';
}

export function hasCityForMap(value: AddressLocationFormValue): boolean {
  return (value.city ?? '').trim() !== '';
}

/** Priority: city → state → country → world (city does not require state). */
export function getMapLocationLevel(
  value: AddressLocationFormValue
): MapLocationLevel {
  if (!hasCountryForMap(value)) {
    return 'world';
  }
  if (hasCityForMap(value)) {
    return 'city';
  }
  if (hasStateForMap(value)) {
    return 'state';
  }
  return 'country';
}

/** Join non-empty location parts for Google Maps (e.g. "VICTORIA, AUSTRALIA"). */
export function combineLocationMapQueryParts(parts: string[]): string {
  return parts
    .map(part => part.trim())
    .filter(part => part.length > 0)
    .join(', ');
}

/**
 * Progressive map query with country context on state/city levels so ambiguous
 * names (e.g. Victoria) resolve in the selected country, not globally.
 */
export function buildProgressiveLocationMapQuery(
  value: AddressLocationFormValue
): string | null {
  const level = getMapLocationLevel(value);
  const country = parseCountryNameForMap(value.countryCode, value.country);

  if (level === 'world' || !country) {
    return null;
  }

  if (level === 'country') {
    return country;
  }

  if (level === 'state') {
    const state = parseStateForMap(value.state);
    return combineLocationMapQueryParts([state, country]) || country;
  }

  const city = parseCityForMap(value.city);
  const state = hasStateForMap(value) ? parseStateForMap(value.state) : '';
  return combineLocationMapQueryParts([city, state, country]) || country;
}

/** Drop invalid/skipped level and resolve the next map query down the priority chain. */
export function buildFallbackLocationMapQuery(
  value: AddressLocationFormValue,
  skipLevel: 'city' | 'state'
): string | null {
  if (skipLevel === 'city') {
    return buildProgressiveLocationMapQuery({ ...value, city: '' });
  }
  return buildProgressiveLocationMapQuery({
    ...value,
    state: '',
    city: '',
  });
}

export function isCityValidForCountry(
  city: string | null | undefined,
  cityList: IDropdownResponse[],
  parseCityName: (value: string | null | undefined) => string = parseCityForMap
): boolean {
  const rawCity = (city ?? '').trim();
  if (!rawCity) {
    return false;
  }
  const normalized = parseCityName(city).toLowerCase();
  const rawNormalized = rawCity.toLowerCase();
  return cityList.some(item => {
    const label = item.cityName ?? item.city ?? item.name ?? '';
    const labelStr = String(label).trim();
    const labelNormalized = parseCityName(labelStr).toLowerCase();
    return (
      labelNormalized === normalized ||
      labelNormalized === rawNormalized ||
      labelStr.toLowerCase() === rawNormalized
    );
  });
}

export function isStateValidForCountry(
  state: string | null | undefined,
  stateList: IDropdownResponse[]
): boolean {
  const formState = (state ?? '').trim();
  if (!formState) {
    return false;
  }
  if (!stateList?.length) {
    return true;
  }
  const formNormalized = formState.toLowerCase();
  const formCode = parseLookupCode(state).toLowerCase();
  const formName = parseLookupDisplayValue(state).toLowerCase();
  return stateList.some(item => {
    const raw = (item.state ?? item.name ?? '').trim();
    const itemNormalized = raw.toLowerCase();
    const itemCode = parseLookupCode(raw).toLowerCase();
    const itemName = parseLookupDisplayValue(raw).toLowerCase();
    return (
      itemNormalized === formNormalized ||
      itemCode === formCode ||
      itemName === formName
    );
  });
}

/** Central map preview decision: priority city → state → country → world. */
export function resolveAddressMapPreview(
  value: AddressLocationFormValue,
  context: AddressMapPreviewContext
): AddressMapPreviewResolution {
  const level = getMapLocationLevel(value);

  if (level === 'world') {
    return { query: null };
  }

  if (level === 'state') {
    const countryCode = parseLookupCode(value.countryCode);
    const stateListReady =
      !countryCode || context.stateListCountryCode === countryCode;

    if (
      context.stateListRequired &&
      !stateListReady &&
      context.stateList.length === 0
    ) {
      return {
        query: buildFallbackLocationMapQuery(value, 'state'),
        needsStateListLoad: true,
      };
    }

    if (
      context.stateList.length > 0 &&
      !isStateValidForCountry(value.state, context.stateList)
    ) {
      return {
        query: buildFallbackLocationMapQuery(value, 'state'),
        showStateValidationError: true,
      };
    }
    return { query: buildProgressiveLocationMapQuery(value) };
  }

  if (level === 'city') {
    const countryCode = parseLookupCode(value.countryCode);
    const cityListReady =
      !countryCode || context.cityListCountryCode === countryCode;

    if (!cityListReady && context.cityValidationList.length === 0) {
      return {
        query: buildFallbackLocationMapQuery(value, 'city'),
        needsCityListLoad: true,
      };
    }

    if (
      context.cityValidationList.length > 0 &&
      !isCityValidForCountry(value.city, context.cityValidationList)
    ) {
      return {
        query: buildFallbackLocationMapQuery(value, 'city'),
        showCityValidationError: true,
      };
    }

    return { query: buildProgressiveLocationMapQuery(value) };
  }

  return { query: buildProgressiveLocationMapQuery(value) };
}
